import { AI_SCORE_LOW_CONFIDENCE_THRESHOLD } from "@/config/aiScoring";
import type { Document } from "@/types/models";
import { parseCellCitations } from "@/utils/parseCitations";
import type { ICellRendererParams } from "ag-grid-community";
import { Check, Loader2 } from "lucide-react";
import { LowConfidenceIcon } from "@/components/LowConfidenceIcon";
import type { ReactNode } from "react";

/** Render **bold** and *italic* markdown as HTML elements. */
function renderInlineMarkdown(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) parts.push(<strong key={match.index}>{match[1]}</strong>);
    else parts.push(<em key={match.index}>{match[2]}</em>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

interface ExtractedDataCellContext {
  documents: Document[];
  toggleExpand: (id: string) => void;
  onViewDocument: (doc: Document, page?: string, sourceText?: string) => void;
}

export const ExtractedDataCell = (params: ICellRendererParams & { context: ExtractedDataCellContext }) => {
  const raw: string = params.value ?? "";
  const documents: Document[] = params.context?.documents ?? [];
  const onViewDocument = params.context?.onViewDocument;

  // Get field status from row data
  const fieldName = params.colDef?.headerName;
  const fieldsData = params.data?.fieldsData as Record<string, { value: string; status: string; aiScore?: number }> | undefined;
  const fieldStatus = fieldName && fieldsData?.[fieldName]?.status;
  const isReviewed = fieldStatus === "Reviewed" || fieldStatus === "Approved";
  const aiScore = fieldName ? fieldsData?.[fieldName]?.aiScore : undefined;
  const isScoreLoading = aiScore === -1;
  const hasLowConfidence = !isScoreLoading && aiScore !== undefined && aiScore < AI_SCORE_LOW_CONFIDENCE_THRESHOLD;

  const { text, citations } = parseCellCitations(raw);

  if (citations.length === 0) {
    return (
      <div className="flex items-start justify-between gap-2 w-full">
        <span className="text-xs flex-1">{renderInlineMarkdown(text)}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isReviewed && (<Check className="w-4 h-4 text-green-600 mt-0.5" />)}
          {isScoreLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" /> : hasLowConfidence && <LowConfidenceIcon />}
        </div>
      </div>
    );
  }

  const parts = text.split(/(\^\d+\^)/);

  return (
    <div className="flex items-start justify-between gap-2 w-full">
      <span className="text-xs leading-relaxed flex-1">
        {parts.map((part, i) => {
          const match = /^\^(\d+)\^$/.exec(part);
          if (match) {
            const num = parseInt(match[1], 10);
            const citation = citations.find((c) => c.number === num);
            if (!citation) return <span key={i}>{part}</span>;

            const doc = documents.find((d) => d.filename === citation.filename);

            return (
              <sup key={i} className="bg-gray-200 px-0.5 py-px mr-0.5 rounded-sm">
                {doc && onViewDocument ? (
                  <button
                    type="button"
                    title={citation.sourceText}
                    className="text-brand-teal hover:underline font-semibold cursor-pointer"
                    onClick={() => {
                      onViewDocument(doc, citation.page, citation.sourceText);
                    }}
                  >
                    {num}
                  </button>
                ) : (
                  <span title={citation.sourceText} className="text-muted-foreground font-semibold">
                    {num}
                  </span>
                )}
              </sup>
            );
          }
          return <span key={i}>{renderInlineMarkdown(part)}</span>;
        })}
      </span>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isReviewed && (<Check className="w-4 h-4 text-green-600 mt-0.5" />)}
        {hasLowConfidence && <LowConfidenceIcon />}
      </div>
    </div>
  );
};
